/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/
import { useState, useEffect, useCallback } from 'react'
import { useRecoilValue } from 'recoil'
import { fetchDataSelector } from '@recoil/selectors/selectors'
import { selectedContractDetails } from '@recoil/atoms/atoms'
import utils from '@utils/utils'
import { ManualReviewAPIEndpoints } from '@pages/manualReviewTab/api'

/**
 * @description Custom hook to manage data fetching and manipulation for the manual review movements grid.
 * @param {object} props - The props for the hook.
 * @param {object} props.gridApi - The AG-Grid API.
 * @param {object} props.gridRef - The ref for the AG-Grid component.
 * @param {object} props.observationDetails - The details of the current observation.
 * @returns {object} An object containing perPageSize, onChangeHandler, and onRefreshClickHandler.
 */
const useManualReviewMovementsData = ({
  gridApi,
  gridRef,
  observationDetails,
}) => {
  const initialState = {
    perPageSize: 100,
  }

  const getCoreData = useRecoilValue(fetchDataSelector)
  const contractDetailsAtom = useRecoilValue(selectedContractDetails)
  const [perPageSize, setPerPageSize] = useState(initialState.perPageSize)

  const getContractIdFromUrl = () => {
    const search =
      typeof window !== 'undefined' ? window?.location?.search || '' : ''
    const rawContractId = new URLSearchParams(search).get('contractId')

    if (!rawContractId) {
      return null
    }

    const decryptedValue = utils?.decryptData(rawContractId) || rawContractId
    const numericValue = Number(decryptedValue)
    return Number.isNaN(numericValue) ? decryptedValue : numericValue
  }

  const onChangeHandler = (event, params) => {
    const ddlValue = params?.value
    const newSize = parseInt(ddlValue)
    setPerPageSize(newSize)

    if (gridRef?.current?.api && gridApi) {
      gridRef.current.api.paginationGoToPage(0)
      gridApi.rowModel.cacheParams.blockSize = newSize
      gridApi.paginationSetPageSize(newSize)
      gridApi.purgeInfiniteCache()
    }
  }

  const onRefreshClickHandler = () => {
    if (!gridApi) return

    const sortModel = gridApi.rowModel?.cacheParams?.sortModel
    const filterModel = gridApi.rowModel?.cacheParams?.filterModel

    let refresh = false
    if (sortModel?.length > 0) {
      gridApi.columnModel?.resetColumnState()
      refresh = true
    }
    if (Object.keys(filterModel)?.length > 0) {
      gridApi.setFilterModel(null)
      refresh = true
    }
    if (!refresh) {
      gridApi.refreshInfiniteCache()
    } else {
      gridApi.purgeInfiniteCache()
    }
  }

  /**
   * @description Fetches the list of ANPR movements for the current VRM and transit date.
   */
  /* istanbul ignore next */
  const getListOfMovementsData = useCallback(() => {
    const dataSource = {
      getRows: (params) => {
        const { startRow, endRow } = params
        gridApi.showLoadingOverlay()
        const pageSize = endRow - startRow
        const pageNumber = params?.endRow / pageSize

        const vrmNo = observationDetails?.vrmNo
        // transitStartTime is an ISO datetime; the endpoint needs YYYY-MM-DD
        const date = String(observationDetails?.transitStartTime || '').slice(
          0,
          10
        )

        if (!vrmNo || !date) {
          params.successCallback([], 0)
          gridApi.showNoRowsOverlay()
          return
        }

        const url = ManualReviewAPIEndpoints?.getMovementsList?.url
        const contractIdFromUrl = getContractIdFromUrl()
        const resolvedContractId =
          contractIdFromUrl ??
          contractDetailsAtom?.contractId ??
          observationDetails?.contractId ??
          observationDetails?.contractID

        const postData = {
          vrmNo: vrmNo,
          contractId: resolvedContractId,
          cctvSiteId: observationDetails?.offenceDetails?.cctvSiteId,
          pageNumber: pageNumber,
          pageSize: pageSize,
        }

        getCoreData(
          ManualReviewAPIEndpoints?.getMovementsList?.method,
          url,
          postData,
          null,
          null,
          true,
          utils?.apiDomainName?.privateLand?.name
        )
          .then((response) => {
            if (response?.status === utils?.apiResponseCode?.successCode) {
              params.successCallback(
                response?.data?.data,
                response?.data?.totalCount
              )
              if (response?.data?.data?.length > 0) {
                gridApi.hideOverlay()
              } else {
                gridApi.showNoRowsOverlay()
              }
            } else {
              params.successCallback([], 0)
              gridApi.showNoRowsOverlay()
            }
          })
          .catch(() => {
            params.successCallback([], 0)
            gridApi.showNoRowsOverlay()
          })
      },
    }
    if (gridApi) {
      gridApi.setDatasource(dataSource)
    }
  }, [gridApi, getCoreData, observationDetails, contractDetailsAtom])

  useEffect(() => {
    if (gridApi) {
      getListOfMovementsData()
    }
  }, [gridApi, getListOfMovementsData])

  return {
    perPageSize,
    onChangeHandler,
    onRefreshClickHandler,
  }
}

export default useManualReviewMovementsData
